import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SiteHeader from '../components/SiteHeader';
import { useAuth } from '../auth/AuthContext';
import { useAuthed } from '../hooks/useAuthed';
import { gymApi } from '../api/gymApi';
import { nextDayLabel } from '../schedule';
import type { ProgramState } from '../../../shared/types';

/** Signed-out landing. Signed-in users get the Today card. */
function SignedOutHome() {
  const navigate = useNavigate();
  return (
    <>
      <SiteHeader />
      <main className="page page--narrow">
        <h1>Gym Coach</h1>
        <p className="muted" style={{ marginTop: 12 }}>
          Run the Pure Bodybuilding PPL, Full Body, or Upper/Lower programs — the app
          tracks which day you're on, every set, and every PR.
        </p>
        <button
          type="button"
          className="btn-primary btn-block btn-lg"
          style={{ marginTop: 24 }}
          onClick={() => navigate('/register')}
        >
          Create account
        </button>
        <button
          type="button"
          className="btn-secondary btn-block"
          style={{ marginTop: 10 }}
          onClick={() => navigate('/login')}
        >
          Sign in
        </button>
      </main>
    </>
  );
}

export default function HomePage() {
  const { user, loading: authLoading } = useAuth();
  const authed = useAuthed();
  const navigate = useNavigate();
  const [state, setState] = useState<ProgramState | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // Two-step confirm, inline rather than a dialog.
  const [confirmUndo, setConfirmUndo] = useState(false);

  const load = useCallback(() => {
    gymApi.getProgramState(authed)
      .then(setState)
      .catch(err => setError(err instanceof Error ? err.message : 'Failed to load your program'));
  }, [authed]);

  useEffect(() => {
    if (!user) return;
    load();
  }, [user, load]);

  if (authLoading) return <p className="muted" style={{ padding: 24 }}>Loading…</p>;
  if (!user) return <SignedOutHome />;

  async function startWorkout() {
    setBusy(true);
    setError('');
    try {
      const session = await gymApi.startWorkout(authed);
      navigate(`/workout/${session.sessionId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start the workout');
      setBusy(false);
    }
  }

  async function undoLast() {
    setBusy(true);
    setError('');
    try {
      const res = await gymApi.undoLastWorkout(authed);
      setState(res.program);
      setConfirmUndo(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not undo the last workout');
    } finally {
      setBusy(false);
    }
  }

  async function skipRest() {
    setBusy(true);
    setError('');
    try {
      setState(await gymApi.advanceDay(authed));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not advance');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <SiteHeader />
      <main className="page page--narrow">
        {error && <p className="error">{error}</p>}
        {!state && !error && <p className="muted">Loading…</p>}

        {state && !state.enrolled && (
          <section className="hero-card">
            <p className="hero-eyebrow">Get started</p>
            <h1 className="hero-title">Choose a program</h1>
            <p className="muted" style={{ margin: '8px 0 20px' }}>
              Pick one of the three training splits to begin.
            </p>
            <button type="button" className="btn-primary btn-block" onClick={() => navigate('/programs')}>
              Browse programs
            </button>
          </section>
        )}

        {state?.enrolled && state.finished && (
          <section className="hero-card">
            <p className="hero-eyebrow">{state.title}</p>
            <h1 className="hero-title">Program complete</h1>
            <p className="muted" style={{ margin: '8px 0 20px' }}>
              You finished all {state.workoutDays} workouts. Nice work.
            </p>
            <button type="button" className="btn-primary btn-block" onClick={() => navigate('/programs')}>
              Start a new program
            </button>
          </section>
        )}

        {state?.enrolled && state.today && (
          <>
            <section className="hero-card">
              <p className="hero-eyebrow">
                {state.title}
                {state.today.week ? ` · ${state.today.week}` : ''}
                {state.today.block ? ` · ${state.today.block.split(':')[0]}` : ''}
              </p>

              {state.today.kind === 'workout' ? (
                <>
                  <p className="hero-label">{nextDayLabel(state.lastCompletedAt)}</p>
                  <h1 className="hero-title">{state.today.label}</h1>
                  <p className="muted" style={{ margin: '6px 0 20px' }}>
                    {state.today.exercises.length} exercises
                  </p>
                  <button
                    type="button"
                    className="btn-primary btn-block btn-lg"
                    onClick={startWorkout}
                    disabled={busy}
                  >
                    {busy ? 'Starting…' : state.activeSessionId ? 'Resume workout' : 'Start workout'}
                  </button>
                </>
              ) : (
                <>
                  <p className="hero-label">{nextDayLabel(state.lastCompletedAt)}</p>
                  <h1 className="hero-title">
                    {state.today.kind === 'optional_rest' ? 'Optional rest day' : 'Rest day'}
                  </h1>
                  <p className="muted" style={{ margin: '6px 0 20px' }}>
                    {state.today.kind === 'optional_rest'
                      ? 'Take it if you need it. Feeling good? Skip ahead to the next workout.'
                      : 'This one is mandatory. Come back tomorrow.'}
                  </p>
                  <button
                    type="button"
                    className={state.today.kind === 'optional_rest' ? 'btn-primary btn-block' : 'btn-secondary btn-block'}
                    onClick={skipRest}
                    disabled={busy}
                  >
                    {busy
                      ? 'Updating…'
                      : state.today.kind === 'optional_rest'
                        ? 'Train anyway →'
                        : 'Mark rest day done'}
                  </button>
                </>
              )}
            </section>

            <section className="stat-row">
              <div className="stat">
                <span className="stat-value">{state.completedWorkouts}</span>
                <span className="stat-label">workouts done</span>
              </div>
              <div className="stat">
                <span className="stat-value">
                  {Math.round((state.completedWorkouts / Math.max(1, state.workoutDays)) * 100)}%
                </span>
                <span className="stat-label">of program</span>
              </div>
              <div className="stat">
                <span className="stat-value">{state.cursor}</span>
                <span className="stat-label">of {state.totalDays} days</span>
              </div>
            </section>

            {state.upcoming.length > 0 && (
              <section>
                <h2>Coming up</h2>
                <ul className="plain-list">
                  {state.upcoming.map(d => (
                    <li key={d.order} className="upcoming-row">
                      <span className={`day-dot day-dot--${d.kind}`} />
                      <span>{d.label}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}

        {state?.enrolled && state.lastCompletedAt != null && !state.activeSessionId && (
          <section className="undo-row">
            {confirmUndo ? (
              <>
                <span className="muted">Reopen your last finished workout?</span>
                <div className="undo-actions">
                  <button type="button" className="btn-quiet btn-sm" onClick={() => setConfirmUndo(false)}>
                    Cancel
                  </button>
                  <button type="button" className="btn-secondary btn-sm" onClick={undoLast} disabled={busy}>
                    {busy ? 'Undoing…' : 'Yes, undo'}
                  </button>
                </div>
              </>
            ) : (
              <button type="button" className="btn-quiet btn-sm" onClick={() => setConfirmUndo(true)}>
                Undo last finished workout
              </button>
            )}
          </section>
        )}

        {state?.enrolled && (
          <nav className="home-nav">
            <button type="button" className="btn-secondary" onClick={() => navigate('/history')}>
              History
            </button>
            <button type="button" className="btn-secondary" onClick={() => navigate('/programs')}>
              Program
            </button>
          </nav>
        )}
      </main>
    </>
  );
}
